import React, { ReactElement } from 'react';
import {
  StyledText,
  StyledTitle,
  StyledTextMobile,
  StyledTitleMobile,
} from './Typography.style';

import useMedia from 'hooks/useMedia';

export type TypographyProps = {
  strong?: string;
  italic?: boolean;
  underline?: boolean;
  marginTop?: string;
  marginBottom?: string;
  paddingRight?: string;
  paddingLeft?: string;
  textAlign?: 'center' | 'left' | 'right' | 'justify';
  children?: any;
};

export interface TitleProps extends TypographyProps {
  level?: 1 | 2 | 3 | 4 | 5 | 6;
}

type TypographyType = React.FC<TypographyProps> & {
  Title: React.FC<TitleProps>;
  Text: React.FC<TypographyProps>;
  children?: any;
};

/**
 * Typography component
 *
 * @prop strong
 * @prop italic
 * @prop underline
 *
 * @usage
 *
 * ```js
 * const { Text, Title } = Typography;
 * ...
 * <>
 *  <Title>Title level 1</Title>
 *  <Title level={2}>Subtitle</Title>
 *  <Text strong>Regular text. Use as <p/></Text>
 *  <Typography strong>Same as <Text /></Typography>
 * </>
 * ```
 */
const Typography: TypographyType = ({ children, ...rest }) => (
  <StyledText {...rest}>{children}</StyledText>
);

const Title: React.FC<TitleProps> = ({ children, level = 1, ...rest }) => {
  const isMobile = useMedia('(max-width: 62rem)');
  const Component = isMobile ? StyledTitleMobile : StyledTitle;

  return (
    <Component as={`h${level}` as any} level={level} {...rest}>
      {children}
    </Component>
  );
};

const Text: React.FC<{ children?: ReactElement }> = ({ children, ...rest }) => {
  const isMobile = useMedia('(max-width: 62rem)');

  const Component = isMobile ? StyledTextMobile : StyledText;

  return <Component {...rest}>{children}</Component>;
};

Typography.Title = Title;
Typography.Text = Text;

export default Typography;
